import { CSSProperties, FC, ReactNode } from 'react';
import { Button, Divider } from 'antd';
import {
    TbLayoutSidebarLeftCollapseFilled,
    TbLayoutSidebarLeftExpandFilled,
    TbLayoutSidebarRightCollapse,
    TbLayoutSidebarRightExpand,
} from 'react-icons/tb';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';

import SmallTooltip from '../../tooltips/SmallTooltip';

export interface ResourceSiderToggleRowProps {
    ariaLabel?: string;
    children?: ReactNode;
    leftSiderLabel?: string;
    leftSiderCollapsed: boolean;
    onToggleLeftSider: () => void;
    onToggleRightSider: () => void;
    rightSiderAvailable?: boolean;
    rightSiderCollapsed: boolean;
    rightSiderLabel?: string;
    rightSeparator?: ReactNode;
    style?: CSSProperties;
}

export type SearchSiderToggleRowProps = ResourceSiderToggleRowProps;

const ResourceSiderToggleRow: FC<ResourceSiderToggleRowProps> = ({
    ariaLabel = 'Resource layout controls',
    children,
    leftSiderLabel = 'Left Sider',
    leftSiderCollapsed,
    onToggleLeftSider,
    onToggleRightSider,
    rightSiderAvailable = false,
    rightSiderCollapsed,
    rightSiderLabel = 'Right Sider',
    rightSeparator,
    style,
}): JSX.Element => {
    const { token } = useAntDesignTheme();
    const leftAction = leftSiderCollapsed ? 'Expand' : 'Collapse';
    const rightAction = rightSiderCollapsed ? 'Expand' : 'Collapse';

    return (
        <div
            aria-label={ariaLabel}
            role='toolbar'
            style={{
                alignItems: 'center',
                backgroundColor: token.colorBgContainer,
                borderBottom: `1px solid ${token.colorBorder}`,
                boxSizing: 'border-box',
                display: 'flex',
                flex: 'none',
                margin: '-0.5rem -0.75rem 0',
                minHeight: '2rem',
                padding: 0,
                ...style,
            }}
        >
            <div
                aria-label='Left sider control'
                style={{
                    alignItems: 'center',
                    alignSelf: 'stretch',
                    display: 'flex',
                    flex: 'none',
                    justifyContent: 'center',
                    padding: '0.25rem 0.75rem 0.25rem 1.25rem',
                }}
            >
                <SmallTooltip padding='0 0.25rem' placement='bottomLeft' title={`${leftAction} ${leftSiderLabel}`}>
                    <Button
                        aria-label={`${leftAction} ${leftSiderLabel.toLocaleLowerCase()}`}
                        icon={
                            leftSiderCollapsed ? (
                                <TbLayoutSidebarLeftExpandFilled style={{ color: token.colorTextTertiary }} />
                            ) : (
                                <TbLayoutSidebarLeftCollapseFilled style={{ color: token.colorTextTertiary }} />
                            )
                        }
                        onClick={onToggleLeftSider}
                        size='small'
                        type='text'
                    />
                </SmallTooltip>
            </div>
            {children ? (
                <>
                    <Divider type='vertical' style={{ borderColor: token.colorBorder, margin: '0 0.5rem' }} />
                    <div style={{ flex: '1 1 auto', minWidth: 0 }}>{children}</div>
                    {rightSiderAvailable
                        ? (rightSeparator ?? (
                              <Divider type='vertical' style={{ borderColor: token.colorBorder, margin: '0 0.5rem' }} />
                          ))
                        : null}
                </>
            ) : (
                <span style={{ flex: '1 1 auto' }} />
            )}
            {rightSiderAvailable ? (
                <div
                    aria-label='Right sider control'
                    style={{
                        alignItems: 'center',
                        alignSelf: 'stretch',
                        display: 'flex',
                        flex: 'none',
                        justifyContent: 'center',
                        padding: '0.25rem 1.25rem 0.25rem 0.75rem',
                    }}
                >
                    <SmallTooltip
                        padding='0 0.25rem'
                        placement='bottomRight'
                        title={`${rightAction} ${rightSiderLabel}`}
                    >
                        <Button
                            aria-label={`${rightAction} ${rightSiderLabel.toLocaleLowerCase()}`}
                            icon={
                                rightSiderCollapsed ? (
                                    <TbLayoutSidebarRightExpand style={{ color: token.colorTextTertiary }} />
                                ) : (
                                    <TbLayoutSidebarRightCollapse style={{ color: token.colorTextTertiary }} />
                                )
                            }
                            onClick={onToggleRightSider}
                            size='small'
                            type='text'
                        />
                    </SmallTooltip>
                </div>
            ) : null}
        </div>
    );
};

const SearchSiderToggleRow: FC<SearchSiderToggleRowProps> = (props) => (
    <ResourceSiderToggleRow
        {...props}
        ariaLabel={props.ariaLabel ?? 'Search layout controls'}
        leftSiderLabel={props.leftSiderLabel ?? 'Left Sider'}
        rightSiderLabel={props.rightSiderLabel ?? 'Right Sider'}
    />
);

export { ResourceSiderToggleRow };
export default SearchSiderToggleRow;
